import { FC } from "react";
import { observer } from "mobx-react-lite";
import { Box, Typography } from "@mui/material";

import { useClaimsStore } from "../../hooks/useClaimsStore";
import {
  formatCurrency,
  formatNumber,
} from "../dashboard/components/observability/MetricTiles";
import type { ClaimsSummary } from "../../types/claims";

/** Each money total carries the number of claims actually contributing to it,
 * i.e. those with a non-zero value in that column -- an 837 with no 835 yet
 * sits at paid_amount=0. Without it a bare total reads as covering every claim
 * in scope when it does not. */
const claimsCount = (count: number): string =>
  `${formatNumber(count)} claim${count === 1 ? "" : "s"}`;

interface SummaryStat {
  label: string;
  value: string;
}

const buildStats = (summary: ClaimsSummary): SummaryStat[] => [
  { label: "Claims", value: formatNumber(summary.totalClaims) },
  {
    label: `Billed · ${claimsCount(summary.billedCount)}`,
    value: formatCurrency(summary.billedAmount),
  },
  {
    label: `Paid · ${claimsCount(summary.paidCount)}`,
    value: formatCurrency(summary.paidAmount),
  },
  {
    label: `Patient Balance · ${claimsCount(summary.patientBalanceCount)}`,
    value: formatCurrency(summary.patientBalance),
  },
  { label: "Appealed", value: formatNumber(summary.appealedCount) },
];

/** Sits directly under the rows, inside ClaimsTable's card, so it reads as the
 * table's own total row rather than a separate panel. */
const FooterStrip: FC<{ children: React.ReactNode; dimmed?: boolean }> = ({
  children,
  dimmed,
}) => (
  <Box
    sx={{
      px: 1.5,
      py: 1,
      borderTop: "1px solid",
      borderColor: "divider",
      display: "flex",
      alignItems: "center",
      flexWrap: "wrap",
      columnGap: 3,
      rowGap: 1,
      flexShrink: 0,
      opacity: dimmed ? 0.6 : 1,
    }}
  >
    {children}
  </Box>
);

/** Totals for every claim the current filters match (REV-2507).
 *
 * The table is paginated, so these cannot be summed from the rows on screen --
 * they come from the backend, aggregated over the whole filtered set. That gap
 * is why the strip says so in words: the rows above show 50, this says 23,893.
 */
const ClaimsQueueSummary: FC = observer(() => {
  const { claimsSummary, isClaimsSummaryLoading } = useClaimsStore();

  // Keep the last totals on screen while a refresh is in flight; only show the
  // placeholder before anything has ever loaded, so changing a filter does not
  // make the row flicker.
  if (!claimsSummary) {
    return isClaimsSummaryLoading ? (
      <FooterStrip>
        <Typography variant="caption" color="text.secondary">
          Calculating totals…
        </Typography>
      </FooterStrip>
    ) : null;
  }

  return (
    <FooterStrip dimmed={isClaimsSummaryLoading}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600 }}
      >
        Total
        <Typography
          component="span"
          variant="caption"
          color="text.secondary"
          sx={{ ml: 0.5, textTransform: "none", letterSpacing: 0, fontWeight: 400 }}
        >
          (all filtered claims, not just this page)
        </Typography>
      </Typography>

      <Box sx={{ flex: 1 }} />

      {buildStats(claimsSummary).map((stat) => (
        <Box key={stat.label} sx={{ minWidth: 0 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            sx={{ lineHeight: 1.2 }}
          >
            {stat.label}
          </Typography>
          <Typography
            variant="body2"
            fontWeight={700}
            sx={{ lineHeight: 1.3, fontVariantNumeric: "tabular-nums" }}
          >
            {stat.value}
          </Typography>
        </Box>
      ))}
    </FooterStrip>
  );
});

export default ClaimsQueueSummary;
